import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Badge,
  MultiSelect,
  SearchInput,
  SortableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableStateRow,
} from "@h2j-data-team/ui";
import type { AnalysisObject, AnalysisUsageRef, ExpressionIssue } from "@repo/shared";
import { DensityToggle } from "@/components/density-toggle";
import { useAnalysisTableFilters, useAnalysisTableSort } from "@/lib/use-analysis-view-preferences";
import { useTableDensity } from "@/lib/use-table-density";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import {
  AnalysisTableCard,
  ExpandableRow,
  ExpressionIssues,
  FlagBadge,
  SEARCH_CLASS,
} from "./analysis-table";
import { usePickerMessages } from "./use-picker-messages";
import { UsageRefBadges } from "./UsageRefBadges";
import { useSheetTitles } from "./use-sheet-titles";

/*
 * The variables, dimensions and measures tables are the same table over three
 * kinds of item: a name, one line of definition, a usage status, and an expansion
 * with the expression issues and where the item is used. Each wrapper maps its
 * rows to `ItemRow`; the scope picks the column names, the stored sort column and
 * the texts (`variables.*`, `dimensions.*`, `measures.*`).
 */

export type ItemsScope = "variables" | "dimensions" | "measures";
type FilterMode = "used" | "unused" | "broken" | "indirect";

export interface ItemRow {
  id: string;
  name: string;
  /** The definition column: a variable's definition, a dimension's fields, a measure's expression. */
  definition: string;
  /** References to the item; the status badge and the sort by usage read it. */
  usage: number;
  used: boolean;
  broken: boolean;
  indirect: boolean;
  orphan: boolean;
  usedIn: AnalysisUsageRef[];
  issues?: ExpressionIssue;
}

type ItemsFilterState = {
  filter: FilterMode[];
  search: string;
};

const FILTER_DEFAULTS: ItemsFilterState = { filter: [], search: "" };

// `nameCol` and `usageCol` are the values the sort preference has always stored
// for the table, so a saved preference keeps working.
const SCOPES: Record<
  ItemsScope,
  {
    nameCol: string;
    usageCol: string;
    modes: FilterMode[];
    nameHeader: string;
    definitionHeader: string;
    statusHeader: string;
    countKey: string;
    unusedKey: string;
    usedInKey: string;
    mono: boolean;
  }
> = {
  variables: {
    nameCol: "name",
    usageCol: "usageCount",
    modes: ["used", "unused", "indirect"],
    nameHeader: "variables.colName",
    definitionHeader: "variables.colDefinition",
    statusHeader: "variables.colStatus",
    countKey: "variables.refCount",
    unusedKey: "variables.unusedBadge",
    usedInKey: "variables.referencedIn",
    mono: true,
  },
  dimensions: {
    nameCol: "title",
    usageCol: "usedInCount",
    modes: ["used", "unused", "broken", "indirect"],
    nameHeader: "dimensions.colTitle",
    definitionHeader: "dimensions.colFields",
    statusHeader: "dimensions.colUsedIn",
    countKey: "dimensions.chartCount",
    unusedKey: "fields.unusedBadge",
    usedInKey: "dimensions.usedIn",
    mono: false,
  },
  measures: {
    nameCol: "title",
    usageCol: "usedInCount",
    modes: ["used", "unused", "broken", "indirect"],
    nameHeader: "measures.colTitle",
    definitionHeader: "measures.colExpression",
    statusHeader: "measures.colUsedIn",
    countKey: "measures.chartCount",
    unusedKey: "fields.unusedBadge",
    usedInKey: "measures.usedIn",
    mono: false,
  },
};

const COLUMN_COUNT = 4;

function matches(row: ItemRow, mode: FilterMode): boolean {
  if (mode === "used") return row.used;
  if (mode === "unused") return !row.used;
  if (mode === "broken") return row.broken;
  return row.indirect;
}

export function ItemsAnalysisTable({
  analysisId,
  scope,
  rows,
  objects,
}: {
  analysisId: string;
  scope: ItemsScope;
  rows: ItemRow[];
  objects: AnalysisObject[];
}) {
  const { t } = useTranslation("analyses");
  const { t: tc } = useTranslation("common");
  const pickerMessages = usePickerMessages();
  const cfg = SCOPES[scope];
  const { density, setDensity } = useTableDensity();
  const { filters, setFilters } = useAnalysisTableFilters<ItemsFilterState>(
    analysisId,
    scope,
    FILTER_DEFAULTS,
  );
  const filter = useMemo(
    () => (Array.isArray(filters.filter) ? filters.filter : []),
    [filters.filter],
  );
  const { search } = filters;
  const { sortBy, sortDir, setSort } = useAnalysisTableSort<string>(scope, {
    sortBy: cfg.nameCol,
    sortDir: "asc",
  });
  const byUsage = sortBy === cfg.usageCol;
  const [expanded, setExpanded] = useState<string | null>(null);
  const sheetTitles = useSheetTitles(objects);
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);

  const filtered = useMemo(() => {
    let result = rows;
    if (filter.length > 0) {
      result = result.filter((row) => filter.some((mode) => matches(row, mode)));
    }
    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (row) =>
          row.name.toLowerCase().includes(lower) || row.definition.toLowerCase().includes(lower),
      );
    }
    return [...result].sort((a, b) => {
      if (byUsage) return sortDir === "asc" ? a.usage - b.usage : b.usage - a.usage;
      return sortDir === "asc" ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
    });
  }, [rows, filter, search, byUsage, sortDir]);

  const handleSort = (col: string) => {
    if (sortBy === col) setSort(col, sortDir === "asc" ? "desc" : "asc");
    else setSort(col, col === cfg.nameCol ? "asc" : "desc");
  };

  const direction = (col: string) => (sortBy === col ? sortDir : undefined);

  const filterOptions = cfg.modes
    .map((mode) => ({ mode, count: rows.filter((row) => matches(row, mode)).length }))
    .filter(({ mode, count }) => !((mode === "broken" || mode === "indirect") && count === 0))
    .map(({ mode, count }) => ({
      value: mode,
      label: t(`${scope}.filter${mode[0].toUpperCase()}${mode.slice(1)}`, { count }),
    }));
  const selectedFilters = filterOptions.filter((o) => filter.includes(o.value)).map((o) => o.value);

  return (
    <AnalysisTableCard
      toolbar={
        <>
          <SearchInput
            ref={searchRef}
            containerClassName={SEARCH_CLASS}
            aria-label={t(`${scope}.searchPlaceholder`)}
            placeholder={t(`${scope}.searchPlaceholder`)}
            clearLabel={tc("list.clearSearch")}
            shortcutHint="/"
            value={search}
            onValueChange={(value) => setFilters({ search: value })}
          />
          <MultiSelect
            size="compact"
            className="w-52"
            aria-label={t("filterByUsage")}
            options={filterOptions}
            value={selectedFilters}
            onValueChange={(next) => setFilters({ filter: next })}
            messages={pickerMessages(t("filterByUsage"))}
            maxDisplay={1}
          />
          <DensityToggle value={density} onChange={setDensity} />
        </>
      }
      footer={
        rows.length > 0 && t(`${scope}.showing`, { filtered: filtered.length, total: rows.length })
      }
    >
      <Table
        className="min-w-[720px]"
        scrollLabel={t(`detail.tabs.${scope}`)}
        scrollClassName="min-h-0 flex-1"
      >
        <TableHeader>
          <TableRow>
            <TableHead className="w-10 px-2">
              <span className="sr-only">{t("table.details")}</span>
            </TableHead>
            <SortableHead
              className="w-[32%]"
              direction={direction(cfg.nameCol)}
              onSort={() => handleSort(cfg.nameCol)}
            >
              {t(cfg.nameHeader)}
            </SortableHead>
            <TableHead>{t(cfg.definitionHeader)}</TableHead>
            <SortableHead
              className="w-[240px]"
              align="end"
              direction={direction(cfg.usageCol)}
              onSort={() => handleSort(cfg.usageCol)}
            >
              {t(cfg.statusHeader)}
            </SortableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 && (
            <TableStateRow
              colSpan={COLUMN_COUNT}
              state="no-results"
              title={t(`${scope}.noMatch`)}
            />
          )}
          {filtered.map((row) => (
            <ExpandableRow
              key={row.id}
              name={row.name}
              expandable={row.usedIn.length > 0 || !!row.issues}
              expanded={expanded === row.id}
              onToggle={() => setExpanded(expanded === row.id ? null : row.id)}
              colSpan={COLUMN_COUNT}
              detail={
                <>
                  {row.issues && <ExpressionIssues scope={scope} issues={row.issues} />}
                  {row.usedIn.length > 0 && (
                    <div>
                      <p className="mb-2 text-xs font-medium text-dim">{t(cfg.usedInKey)}</p>
                      <UsageRefBadges refs={row.usedIn} sheetTitles={sheetTitles} />
                    </div>
                  )}
                </>
              }
            >
              <TableCell wrap className={cfg.mono ? "font-mono font-medium" : "font-medium"}>
                {row.name}
              </TableCell>
              {/* One line of a possibly long expression: the full text is the tooltip. */}
              <TableCell className="font-mono text-dim" title={row.definition || undefined}>
                {row.definition || "—"}
              </TableCell>
              <TableCell align="end">
                <div className="flex flex-nowrap items-center justify-end gap-1">
                  {row.broken && (
                    <Badge variant="danger" className="whitespace-nowrap">
                      {t(`${scope}.brokenBadge`)}
                    </Badge>
                  )}
                  {row.indirect && (
                    <FlagBadge
                      label={t(`${scope}.indirectBadge`)}
                      hint={t(`${scope}.indirectTooltip`)}
                    />
                  )}
                  {row.orphan && (
                    <FlagBadge
                      label={t(`${scope}.orphanBadge`)}
                      hint={t(`${scope}.orphanTooltip`)}
                    />
                  )}
                  {row.used ? (
                    <Badge variant="default" className="font-mono whitespace-nowrap tabular-nums">
                      {t(cfg.countKey, { count: row.usage })}
                    </Badge>
                  ) : (
                    <Badge variant="warning">{t(cfg.unusedKey)}</Badge>
                  )}
                </div>
              </TableCell>
            </ExpandableRow>
          ))}
        </TableBody>
      </Table>
    </AnalysisTableCard>
  );
}
